import React from 'react';

const RecentActivityCard = ({ activity }) => {
  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'quiz_completed':
        return '🏆';
      case 'quiz_started':
        return '📝';
      case 'flashcard_created':
        return '📚';
      case 'flashcard_studied':
        return '🔄';
      case 'study_session_started':
        return '📖';
      case 'study_session_completed':
        return '✅';
      case 'file_uploaded':
        return '📁';
      case 'content_generated':
        return '🤖';
      case 'profile_updated':
        return '👤';
      case 'avatar_changed':
        return '🖼️';
      case 'nickname_changed':
        return '✏️';
      case 'achievement_unlocked':
        return '🏅';
      case 'friend_request_sent':
        return '👥';
      case 'friend_added':
        return '🤝';
      case 'friend_removed':
        return '👋';
      case 'login':
        return '🔑';
      case 'progress_updated':
        return '📈';
      default:
        return '📋';
    }
  };

  const getActivityDescription = (activity) => {
    const { activity_type, activity_subtype, subject, chapter, activity_data } = activity;
    
    switch (activity_type) {
      case 'quiz_completed':
        const score = activity_data?.percentage || 0;
        return `Completed quiz in ${subject || 'Unknown'} with ${score}% score`;
      
      case 'quiz_started':
        return `Started quiz in ${subject || 'Unknown'}`;
      
      case 'flashcard_created':
        const numCards = activity_data?.num_cards || 0;
        return `Created ${numCards} flashcards for ${subject || 'Unknown'}`;
      
      case 'flashcard_studied':
        return `Studied flashcards for ${subject || 'Unknown'}`;
      
      case 'study_session_started':
        return `Started studying ${chapter || subject || 'Unknown'}`;
      
      case 'study_session_completed':
        return `Completed study session for ${chapter || subject || 'Unknown'}`;
      
      case 'file_uploaded':
        const fileName = activity_data?.file_name || 'Unknown file';
        return `Uploaded ${fileName} to ${subject || 'General'}`;
      
      case 'content_generated':
        const contentType = activity_data?.content_type || 'content';
        const numItems = activity_data?.num_items || 0;
        return `Generated ${numItems} ${contentType} for ${subject || 'Unknown'}`;
      
      case 'profile_updated':
        const changeType = activity_data?.change_type;
        if (changeType === 'avatar') return 'Changed profile avatar';
        if (changeType === 'nickname') return 'Updated nickname';
        return 'Updated profile';
      
      case 'achievement_unlocked':
        const achievementName = activity_data?.achievement_name || 'Achievement';
        return `Unlocked achievement: ${achievementName}`;
      
      case 'friend_request_sent':
        return 'Sent friend request';
      
      case 'friend_added':
        return 'Added new friend';
      
      case 'friend_removed':
        return 'Removed friend';
      
      case 'login':
        return 'Logged in';
      
      case 'progress_updated':
        const completion = activity_data?.completion_percentage || 0;
        return `Updated progress to ${completion}% in ${subject || 'Unknown'}`;
      
      default:
        return 'Activity recorded';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-lg">
        {getActivityIcon(activity.activity_type)}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
          {getActivityDescription(activity)}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatTimestamp(activity.created_at)}
        </p>
      </div>
      
      {activity.subject && (
        <div className="flex-shrink-0">
          <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {activity.subject}
          </span>
        </div>
      )}
    </div>
  );
};

export default RecentActivityCard; 